import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { socialService } from '../../../core/services/firebase/socialService';
import { postService } from '../../../core/services/firebase/postService';
import { followService } from '../../../core/services/firebase/followService';
import { SocialProfile } from '../../../core/types/social';
import { SocialPost } from '../../../core/types/social';
import { Loader2, MapPin, Calendar, UserPlus, UserMinus, ShieldAlert, Zap } from 'lucide-react';
import { PostCard } from '../../social/components/PostCard';
import { VerificationBadge } from '../../social/components/VerificationBadge';
import { useAuth } from '../../auth/AuthContext';
import { useSocial } from '../../social/SocialContext';
import { MagicLinkService } from '../../../core/services/auth/MagicLinkService';
import { useNavigate } from 'react-router-dom';

export const SocialProfilePublicPage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile: currentUserProfile } = useSocial();
  
  const [profile, setProfile] = useState<SocialProfile | null>(null);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!handle) return;
      try {
        setLoading(true);
        const data = await socialService.getProfileByHandle(handle);
        setProfile(data);
        setFollowersCount(data?.stats.followers || 0);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [handle]);

  // Load user posts
  useEffect(() => {
    const loadPosts = async () => {
      if (!handle) return;
      try {
        setPostsLoading(true);
        const userPosts = await postService.getPostsByAuthor(handle, 20);
        setPosts(userPosts);
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setPostsLoading(false);
      }
    };
    loadPosts();
  }, [handle]);

  // Check if current user is following this profile
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (currentUserProfile && handle && handle !== currentUserProfile.handle) {
        try {
          const following = await followService.isFollowing(currentUserProfile.handle, handle);
          setIsFollowing(following);
        } catch (error) {
          console.error('Error checking follow status:', error);
        }
      }
    };
    checkFollowStatus();
  }, [currentUserProfile, handle]);

  const handleFollowToggle = async () => {
    if (!currentUserProfile || !handle || isFollowLoading) return;

    setIsFollowLoading(true);
    try {
      if (isFollowing) {
        await followService.unfollow(currentUserProfile.handle, handle);
        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
      } else {
        await followService.follow(currentUserProfile.handle, handle);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert('Erro ao atualizar. Tente novamente.');
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleClaimInitiate = async () => {
    if (!handle) return;
    
    // Check if in Dev Mode
    const isDev = localStorage.getItem('devMode') === 'true';
    
    if (isDev) {
        // Automatically generate a token and redirect to invite for testing
        try {
            const token = await MagicLinkService.generateToken(handle, 'business');
            navigate(`/invite/${token}`);
        } catch (e) {
            console.error('Error generating test token:', e);
            alert('Falha ao gerar token de teste.');
        }
    } else {
        // Production behavior: show contact info or lead form
        alert('Este é um perfil pré-criado. Para reivindicar, entre em contato com comercial@nossapesca.com ou aguarde o convite via WhatsApp.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-midnight">
        <div className="flex-1 flex justify-center items-center">
          <Loader2 className="animate-spin text-brand-gold" size={40} />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-midnight">
        <div className="flex-1 flex flex-col justify-center items-center text-center p-6">
          <div className="text-8xl mb-4 grayscale opacity-20">🤷‍♂️</div>
          <h1 className="text-4xl font-black text-white mb-2 font-display uppercase tracking-tight">Perfil não encontrado</h1>
          <p className="text-white/40 font-medium italic mb-8">O usuário @{handle} não existe ou foi removido.</p>
          <a href="/" className="text-brand-gold hover:underline font-black uppercase tracking-widest text-xs">Voltar para Home</a>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUserProfile?.handle === profile.handle;

  return (
    <div className="min-h-screen flex flex-col bg-brand-midnight text-white">
      
      {/* Shadow Profile Banner */}
      {profile.verification?.isShadow && (
          <div className="bg-brand-gold/10 border-b border-white/10 py-4">
              <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-brand-gold">
                      <ShieldAlert className="flex-shrink-0" size={20} />
                      <p className="text-sm font-black uppercase tracking-widest">
                          Este é um <span className="text-white">Shadow Profile</span> pré-criado com dados do Google/Instagram.
                      </p>
                  </div>
                   <button 
                    onClick={handleClaimInitiate}
                    className="flex items-center gap-2 bg-brand-gold text-brand-midnight px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-glow-gold hover:scale-105"
                  >
                      <Zap size={16} />
                      REIVINDICAR ESTA POUSADA
                  </button>
              </div>
          </div>
      )}

      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Banner / Cover */}
        <div className="h-48 md:h-64 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-t-2xl relative">
            {profile.coverUrl && (
                <img src={profile.coverUrl} alt="Capa" className="w-full h-full object-cover rounded-t-2xl opacity-50" />
            )}
        </div>

         {/* Profile Info Card */}
        <div className="bg-brand-charcoal/30 rounded-3xl border border-white/10 px-8 pb-8 relative -mt-20 mx-4 md:mx-0 z-10 backdrop-blur-3xl">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-8">
                {/* Avatar */}
                <div className="w-40 h-40 rounded-3xl border-4 border-brand-midnight bg-brand-midnight shadow-2xl overflow-hidden flex-shrink-0 relative">
                    {profile.avatarUrl ? (
                        <img src={profile.avatarUrl} alt={profile.displayName} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-4xl">👤</div>
                    )}
                </div>

                {/* Info Text */}
                 <div className="flex-1 pt-4 md:pt-0 pb-2">
                    <div className="flex flex-wrap items-center gap-4 mb-2">
                        <h1 className="text-3xl md:text-4xl font-black text-white leading-tight font-display uppercase tracking-tight">{profile.displayName}</h1>
                        <VerificationBadge 
                            level={profile.verification?.level} 
                            isShadow={profile.verification?.isShadow} 
                            showText 
                            className="mt-1"
                        />
                    </div>
                    <p className="text-brand-gold font-black uppercase tracking-widest text-sm italic">@{profile.handle}</p>
                    
                    {profile.bio && (
                        <p className="text-white/60 mt-4 max-w-2xl font-medium leading-relaxed">{profile.bio}</p>
                    )}

                    <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                            <Calendar size={16} />
                            Entrou em {new Date(profile.createdAt).toLocaleDateString()}
                        </div>
                        {profile.location && (
                             <div className="flex items-center gap-1">
                                <MapPin size={16} />
                                {profile.location.city}, {profile.location.state}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                {!isOwnProfile && currentUserProfile && (
                  <div className="flex gap-3 mt-4 md:mt-0 w-full md:w-auto">
                       <button 
                        onClick={handleFollowToggle}
                        disabled={isFollowLoading}
                        className={`
                          flex-1 md:flex-none px-10 py-3 rounded-xl font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2
                          ${isFollowing 
                            ? 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10' 
                            : 'bg-brand-gold text-brand-midnight shadow-glow-gold hover:scale-105'}
                          disabled:opacity-50 disabled:cursor-not-allowed
                        `}
                      >
                          {isFollowLoading ? (
                            <Loader2 className="animate-spin" size={20} />
                          ) : isFollowing ? (
                            <>
                              <UserMinus size={20} />
                              Seguindo
                            </>
                          ) : (
                            <>
                              <UserPlus size={20} />
                              Seguir
                            </>
                          )}
                      </button>
                  </div>
                )}

                {isOwnProfile && (
                   <div className="flex gap-3 mt-4 md:mt-0 w-full md:w-auto">
                    <a 
                      href="/admin/profile"
                      className="flex-1 md:flex-none bg-white/5 text-white/40 px-10 py-3 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white/10 border border-white/10 transition-all flex items-center justify-center gap-2"
                    >
                      Editar Perfil
                    </a>
                  </div>
                )}
            </div>

             {/* Stats Row */}
            <div className="flex border-t border-white/10 mt-8 pt-8 justify-around md:justify-start md:gap-20">
                <div className="text-center md:text-left">
                    <div className="text-3xl font-black text-white font-display uppercase">{posts.length}</div>
                    <div className="text-[10px] text-white/20 font-black uppercase tracking-[0.2em]">Posts</div>
                </div>
                <div className="text-center md:text-left">
                    <div className="text-3xl font-black text-white font-display uppercase">{followersCount}</div>
                    <div className="text-[10px] text-white/20 font-black uppercase tracking-[0.2em]">Seguidores</div>
                </div>
                <div className="text-center md:text-left">
                    <div className="text-3xl font-black text-white font-display uppercase">{profile.stats.following}</div>
                    <div className="text-[10px] text-white/20 font-black uppercase tracking-[0.2em]">Seguindo</div>
                </div>
            </div>
        </div>

        {/* Content Tabs */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
                 {postsLoading ? (
                  <div className="bg-white/5 p-8 rounded-3xl border border-white/10 flex justify-center py-24">
                    <Loader2 className="animate-spin text-brand-gold" size={40} />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="bg-white/5 p-8 rounded-3xl border border-white/10 text-center py-24">
                      <div className="text-6xl mb-6 grayscale opacity-20">🎣</div>
                      <h3 className="text-2xl font-black text-white mb-2 font-display uppercase tracking-tight">Ainda não há posts</h3>
                      <p className="text-white/40 font-medium italic">
                        {isOwnProfile 
                          ? 'Compartilhe sua primeira pescaria!' 
                          : 'Este usuário ainda não compartilhou relatos de pesca.'}
                      </p>
                       {isOwnProfile && (
                        <a 
                          href="/admin/feed"
                          className="inline-block mt-8 bg-brand-gold text-brand-midnight px-10 py-3 rounded-xl font-black uppercase tracking-widest text-xs shadow-glow-gold hover:scale-105 transition-all"
                        >
                          Criar Post
                        </a>
                      )}
                  </div>
                ) : (
                  posts.map(post => (
                    <PostCard
                      key={post.id}
                      post={post}
                      authorName={profile.displayName}
                      authorAvatar={profile.avatarUrl}
                    />
                  ))
                )}
            </div>

             {/* Sidebar */}
            <div className="space-y-6">
                <div className="bg-brand-charcoal/30 p-8 rounded-3xl border border-white/10">
                    <h3 className="font-black text-xs uppercase tracking-widest text-white/30 mb-6">Conquistas</h3>
                    <div className="flex flex-wrap gap-2">
                        <span className="bg-brand-gold/10 text-brand-gold text-[10px] px-3 py-1.5 rounded-lg font-black uppercase tracking-widest border border-brand-gold/20">🏆 Pescador Iniciante</span>
                        {posts.length >= 5 && (
                          <span className="bg-white/5 text-white/40 text-[10px] px-3 py-1.5 rounded-lg font-black uppercase tracking-widest border border-white/10">📸 Fotógrafo</span>
                        )}
                        {followersCount >= 10 && (
                          <span className="bg-brand-gold/10 text-brand-gold text-[10px] px-3 py-1.5 rounded-lg font-black uppercase tracking-widest border border-brand-gold/20">⭐ Popular</span>
                        )}
                    </div>
                </div>
            </div>
        </div>

      </main>
    </div>
  );
};
